import type { BrandSpecial } from '@iheartjane/data-access';
import {
  JaneGoldCard,
  SPECIALS_GRID_LIMIT,
  SpecialsGrid,
} from '@iheartjane/menu';
import {
  Box,
  Button,
  Flex,
  Loading,
  Skeleton,
  useMobileMediaQuery,
} from '@iheartjane/reefer';
import { useQueryClient } from '@tanstack/react-query';
import type { InfiniteData } from '@tanstack/react-query';

import { useMenu, useUserPreferences } from '@jane/shared-ecomm/providers';
import { trackCashBackDetailPageTap } from '@jane/shared-ecomm/tracking';
import {
  targetedBrandSpecialsKeys,
  useJaneUser,
} from '@jane/shared/data-access';

export const MenuBrandSpecialsGrid = ({
  paginatedBrandSpecials,
  loadingBrandSpecialsPage,
  fetchNextBrandSpecialsPage,
  isFetchingNextBrandSpecialsPage,
  hasNextBrandSpecialsPage,
}: {
  fetchNextBrandSpecialsPage(): void;
  hasNextBrandSpecialsPage: boolean;
  isFetchingNextBrandSpecialsPage: boolean;
  loadingBrandSpecialsPage: boolean;
  paginatedBrandSpecials: BrandSpecial[] | [];
}) => {
  const queryClient = useQueryClient();
  const isMobile = useMobileMediaQuery({});
  const { store, paths } = useMenu();
  const { data: janeUser } = useJaneUser();
  const { userLocation, userPreferences = {} } = useUserPreferences();

  const { city, state, name: storeName, id: storeId } = store;
  const { zipcode } = userLocation;
  const { storeFulfillmentType, storeSearchRadius, storeType } =
    userPreferences;

  const showViewMore = !!paginatedBrandSpecials && hasNextBrandSpecialsPage;
  const showViewLess =
    !showViewMore && paginatedBrandSpecials.length > SPECIALS_GRID_LIMIT;

  const handleResetBrandSpecialsPage = () => {
    queryClient.setQueryData<InfiniteData<unknown>>(
      targetedBrandSpecialsKeys.infiniteBrandSpecials({
        storeIds: [store.id],
        perPage: SPECIALS_GRID_LIMIT,
      }),
      (data) => {
        if (!data) return data;
        return {
          pages: data.pages.slice(0, 1),
          pageParams: data.pageParams.slice(0, 1),
        };
      }
    );
  };

  const handleScroll = () => {
    const element = document.getElementById('brand-specials-grid');
    element?.scrollIntoView({ behavior: 'smooth' });
  };
  const nextBreadcrumbs = [
    {
      title: 'Jane Gold rewards',
      url: paths.menuCategory('jane-gold'),
    },
  ];

  return (
    <Flex flexDirection="column" alignItems="center" px={isMobile ? 24 : 64}>
      <SpecialsGrid id="brand-specials-grid">
        {loadingBrandSpecialsPage && !paginatedBrandSpecials?.length
          ? Array.from({ length: SPECIALS_GRID_LIMIT }, (_, i) => (
              <Skeleton.Bone
                height={240}
                width="100%"
                key={i}
                data-testid="loading-brand-specials-card"
              />
            ))
          : paginatedBrandSpecials.map((brandSpecial, index) => (
              <JaneGoldCard
                authenticated={janeUser?.user?.authenticated}
                brandImage={brandSpecial.product_brand.logo_url}
                brandName={brandSpecial.product_brand.name}
                breadcrumbs={nextBreadcrumbs}
                imageSrc={brandSpecial.photo?.urls.original}
                isBundle={brandSpecial.special_type !== 'product'}
                key={brandSpecial.id}
                onClick={() => {
                  trackCashBackDetailPageTap({
                    brandSpecial,
                    brandDiscountRankInList: index,
                    brandName: brandSpecial.product_brand.name,
                    cityState: `${city}, ${state}`,
                    source: '/jane-gold',
                    storeFulfillmentType,
                    storeId,
                    storeName,
                    storeCity: city,
                    storeState: state,
                    storeSearchRadius,
                    storeType,
                    zipcode,
                  });
                }}
                userSegments={brandSpecial}
                tagLabel={brandSpecial.discount_label}
                title={brandSpecial.title}
                to={paths.menuBrandSpecial({
                  id: brandSpecial.id,
                  title: brandSpecial.title,
                })}
              />
            ))}
      </SpecialsGrid>
      {isFetchingNextBrandSpecialsPage && (
        <Box mt={isMobile ? 24 : 48} position="relative">
          <Loading data-testid="mbs-loader" color="purple" />
        </Box>
      )}
      {!loadingBrandSpecialsPage && (showViewLess || showViewMore) && (
        <Button
          aria-label={
            showViewMore
              ? 'View more Jane Gold rewards'
              : 'View less Jane Gold rewards'
          }
          label={showViewMore ? 'View more' : 'View less'}
          onClick={
            showViewMore
              ? () => fetchNextBrandSpecialsPage()
              : () => {
                  handleResetBrandSpecialsPage();
                  handleScroll();
                }
          }
          full={isMobile}
          mt={isMobile ? 24 : 48}
        />
      )}
    </Flex>
  );
};
